<script setup>
import { useStore } from 'vuex';
import axios from 'axios';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { findTermsAPI } from '@/api/fortuneApi';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { useGesture } from '@/composables/calendar/useGesture';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { isObjectEmpty } from '@/composables/functions';
import { MOBILE_READING_CONTENTS, MOBILE_READING_SECTION_PAGE_MAP } from './constants';

import overviewSajuSummary01 from '@/pages/mobile/analysis-saju-reading/_summary_01.vue';
import overviewSajuSummary02 from '@/pages/mobile/analysis-saju-reading/_summary_02.vue';
import PopDesc from '@/pages/mobile/analysis-saju-reading/_pop_desc.vue';
import summarySub01 from '@/pages/mobile/analysis-saju-reading/_summary_sub_01.vue';
import summarySub02 from '@/pages/mobile/analysis-saju-reading/_summary_sub_02.vue';
import summarySub03 from '@/pages/mobile/analysis-saju-reading/_summary_sub_03.vue';
import summarySub04 from '@/pages/mobile/analysis-saju-reading/_summary_sub_04.vue';
import summarySub05 from '@/pages/mobile/analysis-saju-reading/_summary_sub_05.vue';
import summarySub06 from '@/pages/mobile/analysis-saju-reading/_summary_sub_06.vue';
import summarySub07 from '@/pages/mobile/analysis-saju-reading/_summary_sub_07.vue';
import summarySub09 from '@/pages/mobile/analysis-saju-reading/_summary_sub_09.vue';
import summarySub10 from '@/pages/mobile/analysis-saju-reading/_summary_sub_10.vue';
import summarySub11 from '@/pages/mobile/analysis-saju-reading/_summary_sub_11.vue';
import summarySub12 from '@/pages/mobile/analysis-saju-reading/_summary_sub_12.vue';
import summarySub13 from '@/pages/mobile/analysis-saju-reading/_summary_sub_13.vue';
import summarySub14 from '@/pages/mobile/analysis-saju-reading/_summary_sub_14.vue';
import summarySub15 from '@/pages/mobile/analysis-saju-reading/_summary_sub_15.vue';
import summarySub16 from '@/pages/mobile/analysis-saju-reading/_summary_sub_16.vue';

import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';

import { DEV_MODE } from '@/config/dev-mode';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { storeToRefs } from 'pinia';

definePage({ meta: { layout: 'empty' } });

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();

const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();

const {
  sajuData,
  birthData,
  transdata,
  birthJijanggan,
  daeunsData,
  birthEightTxt,
  sData,
  pSubData,
  baseCode,
} = storeToRefs(analysisDataStore);

const currentPage = ref(0);
const bottomInset = ref(0);
const guestUserId = ref(null);
const infoPage = ref(false);
const totalPages = ref(17); // 총 페이지 수
const showSummary02 = ref(false); // overviewSajuSummary02 표시 여부
const ratingResult = ref(0);
const ratingStatus = ref(false);

const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');

/** 모바일 사용자 토큰 */
const accessToken = ref(null);
const authUserId = ref(null);

const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

const showPopDesc = ref(false);

// PC 버전에서 userId가 있으면 로그인 상태로 판단
if (userId.value) {
  accessToken.value = true;
}

watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) {
      guestUserId.value = newId;
    }
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    if (!isObjectEmpty(sajuData.value)) {
      await fetchSajuDescData();
      await getSajuSummaryRate();
    } else {
      await initializeSajuData();
      await fetchSajuDescData();
      await getSajuSummaryRate();
    }
  }
});

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);

  if (guestUserId.value) getSajuSummaryRate();

  if (window.ReactNativeWebView) {
    if (mobileSajuLnbStore.guestUserId) {
      guestUserId.value = mobileSajuLnbStore.guestUserId;
    }
  } else {
    guestUserId.value = 25184;
  }
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);

  analysisStore.setSelectedContentIndex(null);
});

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // if (data.action === 'refresh') {
    //   sendMessageToApp({ action: 'close' })
    // }

    // message 수신되었을 때
    if (data.action === 'init') {
      accessToken.value = data?.payload?.accessToken;
      authUserId.value = data?.payload?.authUserId;
      mobileSajuLnbStore.setGuestUserId(data?.payload?.guestUserId);
      mobileSajuLnbStore.setUserName(data?.payload?.userName);
      mobileSajuLnbStore.setBottomInset(data?.payload?.bottom);

      axios.defaults.headers.common['Authorization'] = accessToken?.value;
      axios.defaults.headers.common['authUserId'] = authUserId?.value;
    }
    if (data.action === 'reset') {
      resetPopup();
    }
  } catch (e) {
    DEV_MODE && console.error('Invalid message received:', `${e}`);
  }
};

/** 앱으로 메시지 전송 */
const sendMessageToApp = ({ action, message, route }) => {
  if (window.ReactNativeWebView) {
    const messageData = {
      action,
      payload: {
        message,
        route,
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
  }
};

const debuggingHandler = ({ action, message }) => {
  if (DEV_MODE) {
    switch (action) {
      case 'debug':
        console.log(formatConsoleMessage(message));
        break;
      case 'error':
        console.error(formatConsoleMessage(message));
        break;
      default:
        console.warn(`Unknown debug action: ${action}`);
        break;
    }
  }

  sendMessageToApp({ action, message });
};

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const clickNextPage = () => {
  if (currentPage.value >= totalPages.value) goToCoverPage();
  else currentPage.value += 1;
};

const onSwipeLeft = () => {
  if (currentPage.value === 0 || currentPage.value >= totalPages.value) return;
  else currentPage.value += 1;
};

const onSwipeRight = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: onSwipeLeft, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: onSwipeRight, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

// 키보드 방향키로 페이지 이동 (개발용)
const onKeydown = (e) => {
  if (e.key === 'ArrowLeft') clickPrevPage();
  else if (e.key === 'ArrowRight') clickNextPage();
};
onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));

const goToCoverPage = () => {
  currentPage.value = 0;
  showSummary02.value = false;
};

const clickUseSajuAnalysis = () => {
  showSummary02.value = true;
};

const handleNavigateToPage = (tocIdx) => {
  const mappedPage = MOBILE_READING_SECTION_PAGE_MAP[tocIdx];
  if (mappedPage !== undefined) {
    currentPage.value = mappedPage;
  } else {
    DEV_MODE && console.warn('매핑되지 않은 목차 인덱스입니다:', tocIdx);
  }
};

/** 팝업 닫기 */
const close = () => {
  if (window.ReactNativeWebView) {
    router.push('/mobile/saju-lnb');
  } else {
    router.back();
  }
};

const resetPopup = () => {
  currentPage.value = 0;
  infoPage.value = false;
  showSummary02.value = false;
  showPopDesc.value = false;
};

const closePopDesc = () => {
  showPopDesc.value = false;
};

// =============== 사주 데이터 ===============
const { getSajuParams, formatSajuParams, getSajuData } = useSajuData();

/** 사주 데이터 초기화 및 파생값 계산 */
const initializeSajuData = async () => {
  try {
    const sajuParams = await getSajuParams(guestUserId.value);
    if (!sajuParams) return;

    const formattedSajuParams = formatSajuParams(sajuParams, analysisDataStore.paramData);
    const sajuResult = await getSajuData(formattedSajuParams);
    if (!sajuResult) return;

    analysisStore.setUserName(sajuResult?.inputdata?.name);
    analysisDataStore.setSajuData(sajuResult);
  } catch (e) {
    debuggingHandler({ action: 'error', message: `상세분석 > 사주정보 요청 실패: ${e}` });
  }
};

// =============== 컨텐츠 평가 ===============
/** 평가조회 */
const getSajuSummaryRate = async () => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      serviceName: 'SAJU_INTERPRETATION',
      objectKey,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingResult.value = result.score;
      ratingStatus.value = true;
      DEV_MODE && console.log('평가', ratingResult.value, ratingStatus.value);
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      ratingResult.value = 0;
      ratingStatus.value = false;
    }
  }
};

/** 컨텐츠 평가하기 */
const postSajuSummaryRate = async (score) => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      score,
      serviceName: 'SAJU_INTERPRETATION',
      objectKey,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getSajuSummaryRate();
    }
  } catch (error) {}
};

/** 일간 구하기 */
const getDayData = (text) => {
  if (!text) return '';
  const data = text.split('');
  return data[2];
};

const sajuIlgan = computed(() => {
  const result = getDayData(birthEightTxt.value);
  // DEV_MODE && console.log('sajuIlgan computed:', result, 'birthEightTxt:', birthEightTxt.value)
  return result;
});

const isInitializingSajuDesc = ref(false);
/** 사주풀이 데이터 요청 */
const fetchSajuDescData = async () => {
  isInitializingSajuDesc.value = true;
  try {
    const params = {
      guestUserId: guestUserId.value,
      lang: 'KOR',
      birth: sajuData.value.solar ?? null,
      userName: sajuData.value.inputdata.name ?? store.userName,
      bln: sajuData.value.transdata.lucknum ?? null,
      gender: Number(sajuData.value.inputdata.gender) ?? null,
      unknownTime: sajuData.value.inputdata.unknown_time ?? null,
      analysisTypeId: 8,
    };

    if (isObjectEmpty(sData.value)) {
      const result = await fetchBaseServiceAPI(params);

      analysisDataStore.setSData(result.data);
      analysisDataStore.setSajuKeywords(result.data.용어해석);

      await fetchSajuWords(result.data.용어해석);
    }
  } catch (error) {
    DEV_MODE && console.error('fetchSajuDescData 실패,', error);
  } finally {
    isInitializingSajuDesc.value = false;
  }
};

const fetchSajuWords = async (words) => {
  if (words.length > 0) {
    try {
      if (isObjectEmpty(analysisDataStore.dictionaryData)) {
        const params = {
          keywordCategory: '용어사전',
          keyword: words,
        };
        const result = await findTermsAPI(params);

        if (result) analysisDataStore.setDictionaryData(result.data);
      }
    } catch (e) {
      debuggingHandler({
        action: 'error',
        message: `용어사전 데이터 요청 실패: ${JSON.stringify(e)}`,
      });
    }
  }
};

const openFlag = ref('');
const openContentsList = () => {
  if (openFlag.value === '') openFlag.value = 'open';
  else openFlag.value = '';
};
const closeContentsList = () => (openFlag.value = '');
const updateIndexNo = (id) => {
  currentPage.value = id;
  closeContentsList();
};
</script>

<template>
  <div
    class="pop-saju-reading-container mobile"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <AnalysisMobileHeader title="사주풀이" :guestUserId="guestUserId" @close="close" />

    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner">
      <overviewSajuSummary01
        v-if="!showSummary02"
        :guestUserName="analysisDataStore.paramData?.userName || mobileSajuLnbStore?.userName"
      />
      <AnalysisLoadingOverlay
        :loading="showSummary02 && isInitializingSajuDesc"
        :message="'상세 분석중입니다!'"
      />
      <overviewSajuSummary02
        v-if="showSummary02 && !isInitializingSajuDesc"
        :isReady="!isInitializingSajuDesc && !isObjectEmpty(sData)"
        @navigateToPage="handleNavigateToPage"
        @updateShowSummary02="
          (value) => {
            DEV_MODE && console.log('updateShowSummary02 received:', value);
            showSummary02 = value;
          }
        "
      />
      <div class="reading-cover__bottom-box">
        <button class="reading-btn__white" @click="clickUseSajuAnalysis" v-if="!showSummary02">
          사주풀이 시작하기 →
        </button>
      </div>
    </div>

    <div v-if="currentPage !== 0" class="pop-ana-saju-reading-inner st-page">
      <div v-if="currentPage === 1" class="pop-body mobile">
        <summarySub01 />
      </div>
      <div v-if="currentPage === 2" class="pop-body mobile">
        <summarySub02 />
      </div>
      <div v-if="currentPage === 3" class="pop-body mobile">
        <summarySub03
          :birthData="birthData"
          :transdata="transdata"
          :birthJijanggan="birthJijanggan"
          :langMode="langMode"
          :birthTitle="birthTitle"
          :curDaeun="analysisDataStore.curDaeun"
          :selectedData="selectedData"
          :isActive="isActive"
        />
      </div>
      <div v-if="currentPage === 4" class="pop-body mobile">
        <summarySub04 :birthData="birthData" :birthEightTxt="birthEightTxt" />
      </div>
      <div v-if="currentPage === 5" class="pop-body mobile">
        <summarySub05
          :birthData="birthData"
          :birthJijanggan="birthJijanggan"
          :langMode="langMode"
          :birthTitle="birthTitle"
          :transdata="transdata"
        />
      </div>
      <div v-if="currentPage === 6" class="pop-body mobile">
        <summarySub06
          :birthData="birthData"
          :birthJijanggan="birthJijanggan"
          :langMode="langMode"
          :birthTitle="birthTitle"
          :transdata="transdata"
        />
      </div>
      <div v-if="currentPage === 7" class="pop-body mobile">
        <summarySub07
          :langMode="langMode"
          :daeunsData="analysisDataStore.daeunsData"
          :daeunCurNum="analysisDataStore.daeunCurNum"
        />
      </div>
      <div v-if="currentPage === 8" class="pop-body mobile">
        <div class="sr__no-content-page">
          <div class="sr-next__top">
            <span>
              이제, 어렵게 느껴졌던 사주 키워드들이<br />
              조금은 더 편안하고 익숙하게 다가오셨다면?
            </span>
            <span><b>이제 사주팔자의 요약 해석을 만나보세요!</b></span>
          </div>
          <div class="sr-next__bottom">
            <button @click="handleNavigateToPage(2)" class="sr-next__btn">다음 페이지로 →</button>
          </div>
        </div>
      </div>
      <div v-if="currentPage === 9" class="pop-body mobile">
        <summarySub09
          :contentIndex="currentPage"
          :sData="sData"
          :sajuIlgan="sajuIlgan"
          :birthData="birthData"
          :transdata="transdata"
          :sajuData="sajuData"
          :birthJijanggan="birthJijanggan"
          :langMode="langMode"
          :birthTitle="birthTitle"
          :birthEightTxt="birthEightTxt"
        />
      </div>
      <div v-if="currentPage === 10" class="pop-body mobile">
        <summarySub10 :sData="sData" :sajuIlgan="sajuIlgan" :birthData="birthData" />
      </div>
      <div v-if="currentPage === 11" class="pop-body mobile">
        <summarySub11 :sData="sData" :sajuIlgan="sajuIlgan" />
      </div>
      <div v-if="currentPage === 12" class="pop-body mobile">
        <summarySub12
          :sData="sData"
          :sajuIlgan="sajuIlgan"
          :birthData="birthData"
          :transdata="transdata"
          :birthJijanggan="birthJijanggan"
          :curDaeun="curDaeun"
          :langMode="langMode"
          :birthTitle="birthTitle"
        />
      </div>
      <div v-if="currentPage === 13" class="pop-body mobile">
        <summarySub13
          :sData="sData"
          :sajuIlgan="sajuIlgan"
          :birthData="birthData"
          :transdata="transdata"
          :birthJijanggan="birthJijanggan"
          :curDaeun="curDaeun"
          :langMode="langMode"
          :birthTitle="birthTitle"
        />
      </div>
      <div v-if="currentPage === 14" class="pop-body mobile">
        <summarySub14
          :sData="sData"
          :sajuIlgan="sajuIlgan"
          :birthData="birthData"
          :transdata="transdata"
          :birthJijanggan="birthJijanggan"
          :curDaeun="curDaeun"
          :langMode="langMode"
          :birthTitle="birthTitle"
        />
      </div>
      <div v-if="currentPage === 15" class="pop-body mobile">
        <summarySub15 :sData="sData" />
      </div>
      <div v-if="currentPage === 16" class="pop-body mobile">
        <summarySub16 :sData="sData" />
      </div>
      <div v-if="currentPage === 17" class="pop-body mobile">
        <div class="sr__no-content-page">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postSajuSummaryRate"
          />
        </div>
      </div>

      <div class="contentsListBtnBox mobile">
        <div class="list-box" :class="openFlag">
          <ul class="list-li-box">
            <li
              class="li mobile"
              v-for="item in MOBILE_READING_CONTENTS.filter((content) => content.title !== '')"
              :key="item.id"
              @click="updateIndexNo(item.id)"
            >
              {{ item.title }}
            </li>
          </ul>
        </div>
      </div>
      <div class="pop-footer-page-box">
        <div class="pop-desc-box">
          <button class="btn-desc" @click="showPopDesc = true">📖 용어해석</button>
        </div>
        <div class="pop-pagination" v-if="!infoPage && currentPage !== 0">
          <IconList
            @click="openContentsList('open')"
            v-if="openFlag === ''"
            style="margin-top: 3px"
          />
          <IconClose01
            @click="openContentsList('')"
            v-if="openFlag === 'open'"
            style="margin-top: 3px"
          />
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <div class="pop-pagination__index" @click="openContentsList">
            {{ currentPage.toString().padStart(2, '0') }} /
            {{ totalPages.toString().padStart(2, '0') }}
          </div>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 용어해석 팝업 -->
  <PopDesc
    v-model="showPopDesc"
    :sajuKeywords="analysisDataStore.sajuKeywords"
    :dictionaryData="analysisDataStore.dictionaryData"
    @close="closePopDesc"
  />

</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';
.detailed-overview {
  flex-direction: column;
}

.pop-ana-saju-reading-inner {
  height: calc(100vh - 52px);
  overflow-y: auto;
  &.st-page {
    height: calc(100vh - 104px);
  }
  .reading-cover__bottom-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    .reading-btn__white {
      display: flex;
      width: 100%;
      max-width: 500px;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      color: $analysis-saju;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px; /* 133.333% */
    }
  }
  .pop-body {
    height: calc(100vh - 104px);
    padding: 0px;
  }
}
.pop-footer-page-box {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  .pop-desc-box {
    display: flex;
    width: 50%;
    height: 52px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    .btn-desc {
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
      color: var(--gray-100-white, #fff);
      text-align: center;
      font-family: Pretendard;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 18px; /* 128.571% */
      background: var(--orange-300-main, #eb4c10);
    }
  }
  .pop-pagination {
    display: flex;
    height: 52px;
    padding: 14px 20px;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;
    background: var(--Gray-800, #333);
    color: var(--gray-100-white, #fff);
    text-align: center;
    font-family: Pretendard;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 128.571% */
    .pop-pagination__prev,
    .pop-pagination__next {
      width: 32px;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .icon-chevron {
      width: 18px;
      height: 18px;
      mask: url('/src/assets/images_manse/chevron/chevron-left-16.svg') no-repeat center;
      -webkit-mask: url('/src/assets/images_manse/chevron/chevron-left-16.svg') no-repeat center;
      background-size: cover;
      background-color: var(--Gray-400, #c2c2c2);
      mask-size: 100% 100%;
      -webkit-mask-size: 100% 100%;
    }
    .icon-chevron.right {
      transform: rotate(180deg);
    }
  }
}
</style>
